export interface DrinkingDayWindow {
  start: Date;
  end: Date;
}

function zonedParts(at: Date, timezone: string): Record<string, number> {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(at);

  return Object.fromEntries(
    parts.filter((part) => part.type !== 'literal').map((part) => [part.type, Number(part.value)]),
  );
}

function zonedDateToUtc(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second: number,
  timezone: string,
): Date {
  let candidate = new Date(Date.UTC(year, month - 1, day, hour, minute, second));
  for (let iteration = 0; iteration < 4; iteration += 1) {
    const parts = zonedParts(candidate, timezone);
    const representedAsUtc = Date.UTC(
      parts.year ?? year,
      (parts.month ?? month) - 1,
      parts.day ?? day,
      parts.hour ?? hour,
      parts.minute ?? minute,
      parts.second ?? second,
    );
    const targetAsUtc = Date.UTC(year, month - 1, day, hour, minute, second);
    const adjustment = targetAsUtc - representedAsUtc;
    if (adjustment === 0) {
      break;
    }
    candidate = new Date(candidate.getTime() + adjustment);
  }
  return candidate;
}

export function drinkingDayWindow(at: Date, timezone: string, boundaryHour = 6): DrinkingDayWindow {
  const parts = zonedParts(at, timezone);
  const localDate = new Date(Date.UTC(parts.year ?? 1970, (parts.month ?? 1) - 1, parts.day ?? 1));
  if ((parts.hour ?? 0) < boundaryHour) {
    localDate.setUTCDate(localDate.getUTCDate() - 1);
  }

  const start = zonedDateToUtc(
    localDate.getUTCFullYear(),
    localDate.getUTCMonth() + 1,
    localDate.getUTCDate(),
    boundaryHour,
    0,
    0,
    timezone,
  );
  const nextLocalDate = new Date(localDate);
  nextLocalDate.setUTCDate(nextLocalDate.getUTCDate() + 1);
  const endExclusive = zonedDateToUtc(
    nextLocalDate.getUTCFullYear(),
    nextLocalDate.getUTCMonth() + 1,
    nextLocalDate.getUTCDate(),
    boundaryHour,
    0,
    0,
    timezone,
  );

  return {
    start,
    end: new Date(endExclusive.getTime() - 1),
  };
}

export function calendarWeekWindow(
  at: Date,
  timezone: string,
  boundaryHour = 6,
): DrinkingDayWindow {
  const parts = zonedParts(at, timezone);
  const localDate = new Date(Date.UTC(parts.year ?? 1970, (parts.month ?? 1) - 1, parts.day ?? 1));
  const weekday = localDate.getUTCDay();
  localDate.setUTCDate(localDate.getUTCDate() - ((weekday + 6) % 7));
  const start = zonedDateToUtc(
    localDate.getUTCFullYear(),
    localDate.getUTCMonth() + 1,
    localDate.getUTCDate(),
    boundaryHour,
    0,
    0,
    timezone,
  );
  const nextMonday = new Date(localDate);
  nextMonday.setUTCDate(nextMonday.getUTCDate() + 7);
  const endExclusive = zonedDateToUtc(
    nextMonday.getUTCFullYear(),
    nextMonday.getUTCMonth() + 1,
    nextMonday.getUTCDate(),
    boundaryHour,
    0,
    0,
    timezone,
  );
  return { start, end: new Date(endExclusive.getTime() - 1) };
}

function pad(value: number): string {
  return value.toString().padStart(2, '0');
}

export function toZonedDateTimeLocalValue(at: Date, timezone: string): string {
  const parts = zonedParts(at, timezone);
  return `${parts.year ?? 1970}-${pad(parts.month ?? 1)}-${pad(parts.day ?? 1)}T${pad(
    parts.hour ?? 0,
  )}:${pad(parts.minute ?? 0)}`;
}

export function fromZonedDateTimeLocalValue(value: string, timezone: string): Date {
  const match =
    /^(?<year>\d{4})-(?<month>\d{2})-(?<day>\d{2})T(?<hour>\d{2}):(?<minute>\d{2})$/.exec(value);
  if (match?.groups === undefined) {
    throw new RangeError('Der Zeitpunkt ist ungültig.');
  }

  const year = Number(match.groups.year);
  const month = Number(match.groups.month);
  const day = Number(match.groups.day);
  const hour = Number(match.groups.hour);
  const minute = Number(match.groups.minute);
  const candidate = zonedDateToUtc(year, month, day, hour, minute, 0, timezone);
  const represented = zonedParts(candidate, timezone);

  if (
    represented.year !== year ||
    represented.month !== month ||
    represented.day !== day ||
    represented.hour !== hour ||
    represented.minute !== minute
  ) {
    throw new RangeError('Dieser Zeitpunkt existiert in der gewählten Zeitzone nicht.');
  }

  return candidate;
}
